import React from 'react';
import { Link } from 'react-router-dom';
import {
  StyledPostTitle,
  StyledParagraph,
  StyledSectionHeading,
  StyledOL,
} from '../styled';

export default function Article() {
  return (
    <>
      <StyledPostTitle id="discord-football-manager">
        Discord football manager: the social way to run a club
      </StyledPostTitle>
      <StyledParagraph big>
        Most football manager games ask for a quiet evening and a deep
        tactics rabbit hole. A Discord football manager flips that:
        matches, transfers, and banter live where your friends already
        hang out.
      </StyledParagraph>
      <StyledParagraph big>
        The Open League (TOL) is a free-to-play, social football manager
        built around Discord and a simple club portal. Seasons are short.
        Matches sim nightly with text highlights in your server. You
        still make the calls that matter — formation, effort, mindset,
        and deals negotiated with real humans.
      </StyledParagraph>
      <StyledParagraph big>
        If you’ve searched for a football manager on Discord because
        Football Manager is too heavy, or because slash-command “FM
        bots” feel thin, this guide explains what “Discord football
        manager” actually means — and how TOL is different.
      </StyledParagraph>

      <StyledSectionHeading>
        What people mean by “Discord football manager”
      </StyledSectionHeading>
      <StyledParagraph big>
        Three products get lumped into that search:
        <StyledOL>
          <li>
            Invite-a-bot managers — slash commands, packs, ranked
            ladders inside any server. Fast to try; usually
            single-player loops with cosmetic depth.
          </li>
          <li>
            Browser FM lite — web managers with an optional Discord for
            chat.
          </li>
          <li>
            Discord-native leagues — the community is the game:
            negotiations, trash talk, and match reactions happen in
            channels, with a light app for{' '}
            <Link to="/user-guide/players">squad</Link> and tactics.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        TOL sits in camp three. Discord isn’t a marketing channel glued
        on later — it’s where the season is lived.
      </StyledParagraph>

      <StyledSectionHeading>
        How The Open League works (in one screen)
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Daily matches — full matches sim on a schedule; play-by-play
            text streams into Discord.
          </li>
          <li>
            Three-week seasons —{' '}
            <Link to="/user-guide/competitions">promote, relegate</Link>, then{' '}
            <Link to="/user-guide/off-season">youth camp</Link> and deals in the
            break.
          </li>
          <li>
            Human transfers — negotiate with other managers in Discord;
            formalize in the club portal.
          </li>
          <li>
            Simple tactics — formations, effort, mindset. Easy to learn;
            hard to master against people.
          </li>
          <li>
            Play anywhere — Discord + web portal (and stores). Manage
            from your phone between real-life matches.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        New managers: start with the{' '}
        <Link to="/user-guide/start">user guide</Link>, then claim a
        club and join a server timezone that fits you.
      </StyledParagraph>

      <StyledSectionHeading>Discord FM bots vs TOL</StyledSectionHeading>
      <StyledParagraph big>
        Typical Discord FM bot: optional social layer / leaderboard
        flex; AI or quick PvP; infinite grind; pack/NPC market.
      </StyledParagraph>
      <StyledParagraph big>
        The Open League: social is the core loop; persistent clubs in
        shared leagues; fixed ~3-week campaigns; human negotiated
        transfers; light portal + Discord check-ins.
      </StyledParagraph>
      <StyledParagraph big>
        Neither is wrong. Pack-opening dopamine → bot. League story with
        people → TOL.
      </StyledParagraph>

      <StyledSectionHeading>Who TOL is for</StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Football fans who loved FM but not the 400-hour save.
          </li>
          <li>
            Friend groups who already live in Discord and want a shared
            season.
          </li>
          <li>
            Managers who like trading and table drama more than
            pixel-perfect training screens.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        Not for: deepest single-player FM simulation — that’s still
        Football Manager.
      </StyledParagraph>

      <StyledSectionHeading>How to start</StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Open theopenleague.com and register / log in.
          </li>
          <li>
            Join a club in a timezone that matches your evenings.
          </li>
          <li>
            Read{' '}
            <Link to="/user-guide/start">Getting started</Link>, set
            your XI, watch first match night in Discord.
          </li>
          <li>
            Skim <Link to="/press-kit">Press kit</Link> if writing about
            TOL or sharing with a community.
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>FAQ</StyledSectionHeading>
      <StyledParagraph big>
        <strong>Is The Open League free?</strong> Yes — free to play,
        optional convenience features.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Do I need Discord?</strong> Social game (highlights,
        negotiations, community) runs there; club portal is on the web.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Is this the same as Football Manager?</strong> No —
        lighter, multiplayer, Discord-first; not an SI FM clone.
      </StyledParagraph>

      <StyledParagraph big>
        A Discord football manager should feel like matchday with your
        group chat — not another spreadsheet you dread opening. That’s
        the bet behind The Open League.
      </StyledParagraph>

      <StyledParagraph big>
        Ready to manage? <Link to="/">Play TOL now</Link>
        {' — '}
        read the <Link to="/user-guide/start">user guide</Link>
        {' or explore the '}
        <Link to="/press-kit">press kit</Link>.
      </StyledParagraph>
    </>
  );
}
